import {
  getTabSession,
  removeTabSession,
  setTabSession,
} from "@/utils/tabSession";

export type RegistrationOtpChannel = "mobile" | "email";

export type RegistrationOtpSendState = {
  identity: string;
  sendCount: number;
  lastSentAt: number;
  otpId?: string;
};

const OTP_SEND_SESSION_VERSION = 1;
const OTP_SEND_NAMESPACE_PREFIX = "registration-otp";
const OTP_CHANNELS: RegistrationOtpChannel[] = ["mobile", "email"];

const getOtpSendNamespace = (
  flowType: string,
  channel: RegistrationOtpChannel,
): string => `${OTP_SEND_NAMESPACE_PREFIX}:${flowType || "default"}:${channel}`;

const normalizeIdentity = (identity: string): string =>
  identity.replace(/\s/g, "").toLowerCase();

export const getRegistrationOtpIdentity = (
  channel: RegistrationOtpChannel,
  details: {
    countryCode?: string;
    mobileNumber?: string;
    email?: string;
  },
): string => {
  if (channel === "mobile") {
    return normalizeIdentity(
      `${details.countryCode || ""}${details.mobileNumber || ""}`,
    );
  }

  return normalizeIdentity(details.email || "");
};

export const getRegistrationOtpSendState = (
  flowType: string,
  channel: RegistrationOtpChannel,
  identity: string,
): RegistrationOtpSendState | null => {
  if (!identity) return null;

  const stored = getTabSession<RegistrationOtpSendState>(
    getOtpSendNamespace(flowType, channel),
    OTP_SEND_SESSION_VERSION,
  );

  if (!stored || stored.identity !== identity) {
    return null;
  }

  return stored;
};

export const hasRegistrationOtpBeenSent = (
  flowType: string,
  channel: RegistrationOtpChannel,
  identity: string,
): boolean => {
  const stored = getRegistrationOtpSendState(flowType, channel, identity);
  return Boolean(stored && stored.sendCount > 0);
};

export const consumeRegistrationOtpSend = (
  flowType: string,
  channel: RegistrationOtpChannel,
  identity: string,
): RegistrationOtpSendState | null => {
  if (!identity) return null;

  const existing = getRegistrationOtpSendState(flowType, channel, identity);
  const nextState: RegistrationOtpSendState = {
    identity,
    sendCount: (existing?.sendCount ?? 0) + 1,
    lastSentAt: Date.now(),
    otpId: existing?.otpId,
  };

  const saved = setTabSession(
    getOtpSendNamespace(flowType, channel),
    nextState,
    OTP_SEND_SESSION_VERSION,
  );

  return saved ? nextState : (existing ?? nextState);
};

export const saveRegistrationOtpId = (
  flowType: string,
  channel: RegistrationOtpChannel,
  identity: string,
  otpId: string,
): void => {
  if (!identity || !otpId) return;

  const existing = getRegistrationOtpSendState(flowType, channel, identity);
  if (!existing) return;

  setTabSession(
    getOtpSendNamespace(flowType, channel),
    {
      ...existing,
      otpId,
    },
    OTP_SEND_SESSION_VERSION,
  );
};

export const clearRegistrationOtpSendState = (flowType: string): void => {
  OTP_CHANNELS.forEach((channel) => {
    removeTabSession(getOtpSendNamespace(flowType, channel));
  });
};
